import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Modal } from '@/components/Modal/Modal';
import { Tabs } from '@/components/Tabs/Tabs';
import { JobStatusBadge } from '../components/JobStatusBadge';
import { ArtifactView } from '../components/ArtifactView';
import { AttachmentView } from '../components/AttachmentView';
import { RememberOutputsToggle } from '../components/RememberOutputsToggle';
import { JobChatStream } from '../components/JobChatStream';
import { AwaitingInputBanner } from '../components/AwaitingInputBanner';
import { FailedBanner } from '../components/FailedBanner';
import { DeliverableZone } from '../components/DeliverableZone';
import { RunJobSheet } from '../components/RunJobSheet';
import { SaveAsTemplateModal } from '../components/SaveAsTemplateModal';
import {
  useJob,
  useCancelJob,
  useDeleteJob,
  usePauseJob,
  useResumeJob,
  usePostJobMessage,
  useAnswerJobInput,
  useAcknowledgeJob,
  useSetJobLastSeen,
} from '../hooks';
import { usePermission, useAuthStore } from '@/stores/auth';
import { attachmentDownloadUrl, attachmentInlineUrl } from '@/features/attachments';
import { t } from '@/lib/i18n';
import { formatDateTime } from '@/lib/format';
import type { AnswerJobInputBody, Artifact, JobAttachment, JobDetail } from '../schemas';
import styles from './JobDetailPage.module.css';

type DetailModalState =
  | { kind: 'input'; name: string; value: unknown }
  | { kind: 'output'; artifact: Artifact }
  | { kind: 'attachment'; attachment: JobAttachment }
  | null;

export function JobDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  const tenantUserId = useAuthStore((s) => s.tenantUser?.id);
  const { data: job, isLoading } = useJob(id);
  const canCancel = usePermission('cancel_job');
  const canChat = usePermission('chat_with_member');
  const canCreateJob = usePermission('create_job');
  const cancel = useCancelJob(id);
  const deleteJob = useDeleteJob(id);
  const pause = usePauseJob(id);
  const resume = useResumeJob(id);
  const postMessage = usePostJobMessage(id);
  const answerInput = useAnswerJobInput(id);
  const acknowledge = useAcknowledgeJob(id);
  const setLastSeen = useSetJobLastSeen(id);
  const [detail, setDetail] = useState<DetailModalState>(null);
  const [composerText, setComposerText] = useState('');
  const [runAgainOpen, setRunAgainOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const lastSeenWriteRef = useRef<number | null>(null);
  useEffect(() => {
    if (!job) return;
    const maxTurn = job.messages.reduce((m, x) => Math.max(m, x.turn_number), 0);
    const seen = job.last_seen_turn_number ?? 0;
    if (maxTurn > seen && lastSeenWriteRef.current !== maxTurn) {
      lastSeenWriteRef.current = maxTurn;
      setLastSeen.mutate(maxTurn);
    }
  }, [job, setLastSeen]);

  if (isLoading || !job) return <div className={styles.state}>{t('Loading…')}</div>;

  const isPaused = Boolean(job.paused_at);
  const isAwaitingInput = job.status === 'awaiting_input';
  const isFailed = job.status === 'failed';
  const isCompleted = job.status === 'completed';
  const isCancelled = job.status === 'cancelled';
  const isInFlight = job.status === 'running' || isAwaitingInput;
  const chatLocked = isFailed || isCancelled;
  const memberName = job.assigned_member.display_name;

  const defaultTab = isCompleted && job.artifacts.length > 0 ? 'deliverables' : 'activity';
  const activeTab = searchParams.get('tab') ?? defaultTab;

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab }, { preventScrollReset: true });
  };

  const handleSend = () => {
    const text = composerText.trim();
    if (!text || !canChat) return;
    postMessage.mutate({ text }, { onSuccess: () => setComposerText('') });
  };

  const handleAnswerInput = (body: AnswerJobInputBody) => {
    answerInput.mutate(body);
  };

  return (
    <div className={styles.page}>
      {/* ─── Top bar ──────────────────────────────────────── */}
      <div className={styles.topBar}>
        <Link to="/jobs" className={styles.back}>
          <Button variant="ghost" size="sm">
            {t('← All jobs')}
          </Button>
        </Link>
      </div>

      {/* ─── Header ───────────────────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <h1 className={styles.title}>{job.title}</h1>
          <div className={styles.headerActions}>
            {job.status === 'running' && !isPaused && canChat ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => pause.mutate()}
                disabled={pause.isPending}
              >
                {t('⏸ Pause')}
              </Button>
            ) : null}
            {job.status === 'running' && isPaused && canChat ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => resume.mutate({})}
                disabled={resume.isPending}
              >
                {t('▶ Resume')}
              </Button>
            ) : null}
            {isInFlight && canCancel ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
              >
                {t('Cancel')}
              </Button>
            ) : null}
            {(isCompleted || isCancelled) && job.created_from_template_id ? (
              <Button variant="secondary" size="sm" onClick={() => setRunAgainOpen(true)}>
                {t('Run again')}
              </Button>
            ) : null}
            {(isCompleted || isCancelled || isFailed) && canCancel ? (
              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(true)}>
                {t('Delete')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className={styles.headerMeta}>
          <span className={styles.memberChip}>
            <Avatar name={memberName} size="sm" kind="ai" />
            <span>{memberName}</span>
          </span>
          <StatusLine job={job} />
        </div>
        <div className={styles.headerSubline}>
          {t('Started by {name}, {time}', {
            name: job.created_by_tenant_user.display_name,
            time: formatDateTime(job.created_at, { hour: 'numeric', minute: '2-digit' }),
          })}
          {job.completed_at
            ? ` · ${t('Finished {time}', {
                time: formatDateTime(job.completed_at, {
                  hour: 'numeric',
                  minute: '2-digit',
                }),
              })}`
            : null}
        </div>
        {job.status === 'running' && !isPaused && !isAwaitingInput ? (
          <div className={styles.headerSubline}>
            <strong>{t('Consider it handled.')}</strong>{' '}
            {t("{name}'s on it.", { name: memberName })}
          </div>
        ) : null}
      </header>

      {/* ─── Banners (above tab bar, always visible) ──────── */}
      {isAwaitingInput && job.pending_input_request && canChat ? (
        <AwaitingInputBanner
          request={job.pending_input_request}
          isSubmitting={answerInput.isPending}
          onAnswer={handleAnswerInput}
          onSwitchToChat={() => composerRef.current?.focus()}
        />
      ) : null}
      {isFailed && !job.acknowledged_at ? (
        <FailedBanner
          errorMessage={job.error_message}
          jobId={job.id}
          templateId={job.created_from_template_id}
          memberName={memberName}
          onAbandon={() => acknowledge.mutate()}
        />
      ) : null}
      {isCompleted && job.created_from_template_id === null && canCreateJob && !bannerDismissed ? (
        <div className={styles.saveTemplateBanner}>
          <div className={styles.saveTemplateBannerText}>
            <div className={styles.saveTemplateBannerTitle}>{t('Did this work well?')}</div>
            <div className={styles.saveTemplateBannerBody}>
              {t('Save it as a template and run it again any time.')}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setSaveTemplateOpen(true)}
          >
            {t('Save as template →')}
          </Button>
        </div>
      ) : null}

      {/* ─── Tabs ─────────────────────────────────────────── */}
      <div className={styles.tabs}>
        <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Trigger value="activity">{t('Activity')}</Tabs.Trigger>
            <Tabs.Trigger value="deliverables">
              {t('Deliverables')}
              {job.artifacts.length > 0 ? (
                <span className={styles.tabBadge}>{job.artifacts.length}</span>
              ) : null}
            </Tabs.Trigger>
            <Tabs.Trigger value="details">{t('Details')}</Tabs.Trigger>
          </Tabs.List>

          {/* Activity */}
          <Tabs.Content value="activity">
            <div className={styles.activityPanel}>
              <ChatScrollBody messageCount={job.messages.length}>
                <JobChatStream
                  messages={job.messages}
                  viewer={{ tenantUserId: tenantUserId ?? '' }}
                  member={{ displayName: memberName }}
                  userNames={buildUserNamesMap(job)}
                  lastSeenTurnNumber={job.last_seen_turn_number}
                  isProcessing={job.status === 'running' && !isPaused}
                />
              </ChatScrollBody>
              {canChat ? (
                <div className={styles.composer}>
                  <textarea
                    ref={composerRef}
                    className={styles.composerInput}
                    placeholder={
                      chatLocked
                        ? t('Chat is not available on {status} jobs.', { status: job.status })
                        : t('Message {name}', { name: memberName })
                    }
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={2}
                    disabled={postMessage.isPending || chatLocked}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSend}
                    disabled={composerText.trim() === '' || postMessage.isPending || chatLocked}
                  >
                    {t('Send')}
                  </Button>
                </div>
              ) : (
                <div className={styles.composerReadOnly}>
                  {t('Read-only access — ask your admin for Contributor access to chat.')}
                </div>
              )}
            </div>
          </Tabs.Content>

          {/* Deliverables */}
          <Tabs.Content value="deliverables">
            <div className={styles.deliverablesPanel}>
              {job.artifacts.length > 0 ? (
                <DeliverableZone
                  artifacts={job.artifacts}
                  emptyMessage=""
                  onOpen={(a) => {
                    if (a.mime_type === 'text/html') {
                      openHtmlArtifactInNewWindow(a);
                    } else {
                      setDetail({ kind: 'output', artifact: a });
                    }
                  }}
                />
              ) : isInFlight ? (
                <div className={styles.emptyState}>
                  <div className={styles.pulseRing}>
                    <span aria-hidden="true">📄</span>
                  </div>
                  <p className={styles.emptyTitle}>
                    {t('{name} is working on it', { name: memberName })}
                  </p>
                  <p className={styles.emptySub}>
                    {t("Deliverables will appear here as they're finished.")}
                  </p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon} aria-hidden="true">
                    📭
                  </span>
                  <p className={styles.emptyTitle}>{t('No deliverables produced')}</p>
                  <p className={styles.emptySub}>
                    {t("{name} didn't finish any deliverables before this job ended.", {
                      name: memberName,
                    })}
                  </p>
                </div>
              )}
            </div>
          </Tabs.Content>

          {/* Details */}
          <Tabs.Content value="details">
            <div className={styles.detailsPanel}>
              <div className={styles.detailsLeft}>
                <section className={styles.detailSection}>
                  <div className={styles.detailEyebrow}>{t('Job info')}</div>
                  <div className={styles.detailBlock}>
                    <dl className={styles.jobInfo}>
                      <div className={styles.jobInfoRow}>
                        <dt className={styles.jobInfoKey}>{t('Member')}</dt>
                        <dd className={styles.jobInfoVal}>
                          <Link
                            to={`/team/m/${encodeURIComponent(job.assigned_member.id)}`}
                            className={styles.memberLink}
                          >
                            <Avatar name={memberName} size="sm" kind="ai" />
                            {memberName}
                          </Link>
                        </dd>
                      </div>
                      <div className={styles.jobInfoRow}>
                        <dt className={styles.jobInfoKey}>{t('Started by')}</dt>
                        <dd className={styles.jobInfoVal}>
                          <span>{job.created_by_tenant_user.display_name}</span>
                          {' · '}
                          {formatDateTime(job.created_at, {
                            hour: 'numeric',
                            minute: '2-digit',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </dd>
                      </div>
                      {job.completed_at ? (
                        <div className={styles.jobInfoRow}>
                          <dt className={styles.jobInfoKey}>{t('Finished')}</dt>
                          <dd className={styles.jobInfoVal}>
                            {formatDateTime(job.completed_at, {
                              hour: 'numeric',
                              minute: '2-digit',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </dd>
                        </div>
                      ) : null}
                      <div className={styles.jobInfoRow}>
                        <dt className={styles.jobInfoKey}>{t('Template')}</dt>
                        <dd className={styles.jobInfoVal}>
                          {job.created_from_template_id ? (
                            <Link
                              to={`/library/job-profiles/${encodeURIComponent(job.created_from_template_id)}`}
                              className={styles.memberLink}
                            >
                              {t('View template')}
                            </Link>
                          ) : (
                            <span className={styles.muted}>{t('Ad-hoc job')}</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </section>

                <section className={styles.detailSection}>
                  <div className={styles.detailEyebrow}>{t('Instructions')}</div>
                  <div className={styles.detailBlock}>
                    <div
                      className={styles.instructionsMarkdown}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          marked.parse(job.instructions, { async: false }),
                        ),
                      }}
                    />
                  </div>
                </section>
              </div>

              <div className={styles.detailsRight}>
                <section className={styles.detailSection}>
                  <div className={styles.detailEyebrow}>{t('Inputs')}</div>
                  <div className={styles.detailBlock}>
                    {Object.entries(job.inputs).length === 0 && job.attachments.length === 0 ? (
                      <div className={styles.cardMuted}>{t('No inputs.')}</div>
                    ) : (
                      <ul className={styles.fieldList}>
                        {Object.entries(job.inputs).map(([name, value]) => (
                          <li key={name}>
                            <button
                              type="button"
                              className={styles.fieldRow}
                              onClick={() => setDetail({ kind: 'input', name, value })}
                            >
                              <span className={styles.fieldIcon} aria-hidden="true">
                                📄
                              </span>
                              <span className={styles.fieldBody}>
                                <span className={styles.fieldLabel}>{name}</span>
                                <span className={styles.fieldPreview}>{previewValue(value)}</span>
                              </span>
                            </button>
                          </li>
                        ))}
                        {tenantId
                          ? job.attachments.map((a) => (
                              <li key={a.id}>
                                <AttachmentRow
                                  tenantId={tenantId}
                                  attachment={a}
                                  onOpenInModal={() =>
                                    setDetail({ kind: 'attachment', attachment: a })
                                  }
                                />
                              </li>
                            ))
                          : null}
                      </ul>
                    )}
                  </div>
                </section>

                <section className={styles.detailSection}>
                  <RememberOutputsToggle jobId={job.id} current={job.remember_outputs} />
                </section>
              </div>
            </div>
          </Tabs.Content>
        </Tabs.Root>
      </div>

      {/* ─── Detail modal ─────────────────────────────────── */}
      <Modal
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
        size="xl"
        title={
          detail?.kind === 'input'
            ? detail.name
            : detail?.kind === 'output'
              ? (detail.artifact.parameter_name ?? t('Output'))
              : detail?.kind === 'attachment'
                ? detail.attachment.name
                : ''
        }
        description={
          detail?.kind === 'output'
            ? `${detail.artifact.mime_type} · ${formatBytes(detail.artifact.size_bytes)}`
            : detail?.kind === 'attachment'
              ? `${detail.attachment.mime_type} · ${formatBytes(detail.attachment.size_bytes)}`
              : undefined
        }
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                if (detail?.kind === 'input') downloadInput(detail.name, detail.value);
                if (detail?.kind === 'output') downloadArtifact(detail.artifact);
                if (detail?.kind === 'attachment' && tenantId) {
                  window.open(attachmentDownloadUrl(tenantId, detail.attachment.id), '_blank');
                }
              }}
            >
              {t('Download')}
            </Button>
            <Button variant="primary" onClick={() => setDetail(null)}>
              {t('Close')}
            </Button>
          </>
        }
      >
        {detail?.kind === 'input' ? <InputValueView value={detail.value} /> : null}
        {detail?.kind === 'output' ? <ArtifactView artifact={detail.artifact} /> : null}
        {detail?.kind === 'attachment' && tenantId ? (
          <AttachmentView tenantId={tenantId} attachment={detail.attachment} />
        ) : null}
      </Modal>

      {runAgainOpen && job.created_from_template_id ? (
        <RunJobSheet
          open
          onOpenChange={(o) => {
            if (!o) setRunAgainOpen(false);
          }}
          templateId={job.created_from_template_id}
          prefillInputs={job.inputs}
          prefillMemberId={job.assigned_member.id}
        />
      ) : null}
      {saveTemplateOpen ? (
        <SaveAsTemplateModal
          open
          onOpenChange={setSaveTemplateOpen}
          jobId={job.id}
          initialTitle={job.title}
          instructions={job.instructions}
          onSaved={(templateId) => {
            setBannerDismissed(true);
            void navigate(`/library/job-profiles/${encodeURIComponent(templateId)}`);
          }}
        />
      ) : null}
      <Modal
        open={deleteConfirm}
        onOpenChange={(open) => {
          if (!open && !deleteJob.isPending) setDeleteConfirm(false);
        }}
        title={t('Delete this job?')}
        description={t(
          'This will permanently remove the job, its deliverables, and its chat history. This cannot be undone.',
        )}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(false)}
              disabled={deleteJob.isPending}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                deleteJob.mutate(undefined, { onSuccess: () => void navigate('/jobs') })
              }
              isLoading={deleteJob.isPending}
            >
              {deleteJob.isPending ? t('Deleting…') : t('Delete')}
            </Button>
          </>
        }
      />
    </div>
  );
}

/* ─── Sub-components / helpers ──────────────────────────────── */

function AttachmentRow({
  tenantId,
  attachment,
  onOpenInModal,
}: {
  tenantId: string;
  attachment: JobAttachment;
  onOpenInModal: () => void;
}) {
  const sharedInner = (
    <>
      <span className={styles.fieldIcon} aria-hidden="true">
        📎
      </span>
      <span className={styles.fieldBody}>
        <span className={styles.fieldLabel}>{attachment.name}</span>
        <span className={styles.fieldPreview}>
          {attachment.mime_type} · {formatBytes(attachment.size_bytes)}
        </span>
      </span>
    </>
  );
  if (attachment.mime_type === 'text/html') {
    return (
      <a
        className={styles.fieldRow}
        href={attachmentInlineUrl(tenantId, attachment.id)}
        target="_blank"
        rel="noreferrer"
      >
        {sharedInner}
      </a>
    );
  }
  return (
    <button type="button" className={styles.fieldRow} onClick={onOpenInModal}>
      {sharedInner}
    </button>
  );
}

function openHtmlArtifactInNewWindow(artifact: Artifact): void {
  const blob = new Blob([artifact.content], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) {
    alert('Popup blocked. Please allow popups for this site to view HTML results.');
  }
}

function ChatScrollBody({
  messageCount,
  children,
}: {
  messageCount: number;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messageCount]);
  return (
    <div ref={ref} className={styles.chatBody}>
      {children}
    </div>
  );
}

function StatusLine({ job }: { job: JobDetail }) {
  const runtime = runtimeLabel(job);
  return (
    <span className={styles.statusLine}>
      <JobStatusBadge status={job.status} />
      {runtime ? <span className={styles.runtime}>{runtime}</span> : null}
    </span>
  );
}

function runtimeLabel(job: JobDetail): string {
  if (!job.started_at) return '';
  const start = Date.parse(job.started_at);
  const end = job.completed_at ? Date.parse(job.completed_at) : Date.now();
  const ms = end - start;
  const minutes = Math.floor(ms / 60_000);
  if (job.status === 'running' || job.status === 'awaiting_input') {
    return t('{m}m elapsed', { m: String(minutes) });
  }
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${minutes}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function buildUserNamesMap(job: JobDetail): Record<string, string> {
  return { ...(job.user_names ?? {}) };
}

function InputValueView({ value }: { value: unknown }) {
  if (typeof value === 'string') {
    return <p className={styles.modalText}>{value}</p>;
  }
  return <pre className={styles.modalPre}>{JSON.stringify(value, null, 2)}</pre>;
}

function previewValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 60 ? `${value.slice(0, 60)}…` : value;
  }
  return JSON.stringify(value);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadInput(name: string, value: unknown): void {
  const safeName = sanitizeFilename(name);
  if (typeof value === 'string') {
    triggerDownload(`${safeName}.txt`, 'text/plain;charset=utf-8', value);
    return;
  }
  triggerDownload(
    `${safeName}.json`,
    'application/json;charset=utf-8',
    JSON.stringify(value, null, 2),
  );
}

function downloadArtifact(artifact: Artifact): void {
  const base = sanitizeFilename(artifact.parameter_name ?? `output-${artifact.id.slice(0, 8)}`);
  const ext = mimeToExtension(artifact.mime_type);
  triggerDownload(`${base}.${ext}`, `${artifact.mime_type};charset=utf-8`, artifact.content);
}

function triggerDownload(filename: string, mimeType: string, content: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function sanitizeFilename(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'download'
  );
}

function mimeToExtension(mime: string): string {
  if (mime === 'text/markdown') return 'md';
  if (mime === 'application/json') return 'json';
  if (mime === 'text/html') return 'html';
  if (mime === 'text/csv') return 'csv';
  if (mime === 'text/plain') return 'txt';
  if (mime.startsWith('text/')) return 'txt';
  return 'bin';
}
